// The DECISION panel — owner of the card's ephemeral staging state (brief §3.5: staging is view
// state ONLY; there is no durable approved-but-unfired status). It renders one DecisionBlock per
// choice set / standalone proposal, the automatic follow-on note (the declared post-execution
// move — nothing to approve), and the Execute bar. Execute compiles the ticks into the ONE
// payload-bearing commit and routes it through the injected transport; on ok OR on a
// verdict_conflict / inconsistent_staging refusal the staging clears and the surface re-reads the
// card (never optimistic, never a blind retry — brief §3.5). The panel sets NO approval field
// (P-GATE); `canRule` only gates the rendering of controls (G-180).

import { useState } from 'react';
import type { DecisionBlockView, ExecuteResult, StagedVerdictWire } from '../projections.ts';
import { WorkSectionHead } from '../work-ui.tsx';
import { DecisionBlock } from './DecisionBlock.tsx';
import { ExecuteBar } from './ExecuteBar.tsx';
import {
  blockKeyOf,
  compileStaged,
  findOption,
  openOptionsOf,
  selectedOptionOf,
  stagedInconsistency,
  stagedSummaryOf,
  type SelectionMap,
  type StageMap,
  type StageTick,
} from './staging.ts';

export interface DecisionPanelProps {
  readonly decisions: readonly DecisionBlockView[];
  /** The item's current state — the from-side of the automatic follow-on note. */
  readonly itemState: string;
  readonly canRule: boolean;
  /** Route the ONE commit; the surface re-reads on ok/conflict and returns the honest result. */
  readonly onExecute: (staged: readonly StagedVerdictWire[]) => Promise<ExecuteResult>;
}

/** The staging view-state + the one commit routine (kept out of the JSX for the size ceiling). */
function useCardStaging(
  decisions: readonly DecisionBlockView[],
  onExecute: DecisionPanelProps['onExecute'],
): {
  readonly selection: SelectionMap;
  readonly stage: StageMap;
  readonly busy: boolean;
  readonly note: string | null;
  readonly select: (key: string, optionId: string) => void;
  readonly stageTick: (key: string, tick: StageTick | null) => void;
  readonly commit: () => Promise<void>;
} {
  const [selection, setSelection] = useState<SelectionMap>({});
  const [stage, setStage] = useState<StageMap>({});
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const clear = (): void => {
    setStage({});
    setSelection({});
  };
  const commit = async (): Promise<void> => {
    const staged = compileStaged(decisions, selection, stage);
    if (busy || staged.length === 0 || stagedInconsistency(decisions, selection, stage) !== null)
      return;
    setBusy(true);
    setNote(null);
    try {
      const result = await onExecute(staged);
      if (result.ok) return clear();
      setNote(result.note ?? 'That commit was not accepted.');
      const conflicted =
        result.conflict ||
        result.outcomes.some(
          (row) => row.outcome === 'verdict_conflict' || row.outcome === 'inconsistent_staging',
        );
      // The round changed underneath — the stale ticks clear; the surface has re-read the card.
      if (conflicted) clear();
    } finally {
      setBusy(false);
    }
  };
  return {
    selection,
    stage,
    busy,
    note,
    select: (key, optionId) => setSelection((prev) => ({ ...prev, [key]: optionId })),
    stageTick: (key, tick) => setStage((prev) => ({ ...prev, [key]: tick ?? undefined })),
    commit,
  };
}

/** The declared post-execution moves across the open round, rendered as honest follow-on notes. */
function FollowOnNotes({
  decisions,
  itemState,
}: {
  readonly decisions: readonly DecisionBlockView[];
  readonly itemState: string;
}): React.JSX.Element {
  const moves: string[] = [];
  for (const block of decisions) {
    for (const option of openOptionsOf(block)) {
      const move = option.postExecutionMove;
      if (move !== null && move !== itemState && !moves.includes(move)) moves.push(move);
    }
  }
  return (
    <>
      {moves.map((move) => (
        <div
          key={move}
          data-followon={move}
          className="rounded-md border border-dashed border-border bg-surface-sunken px-4 py-3 text-sm text-text-muted"
        >
          <span className="ritona-numeric mr-2 text-xs uppercase tracking-eyebrow text-text-subtle">
            Automatic follow-on
          </span>
          {itemState} → {move} · the platform appends this move when the approved action fires —
          nothing to approve.
        </div>
      ))}
    </>
  );
}

/** The decision blocks + automatic follow-on + the one Execute bar (brief §5.3.2). */
export function DecisionPanel({
  decisions,
  itemState,
  canRule,
  onExecute,
}: DecisionPanelProps): React.JSX.Element {
  const staging = useCardStaging(decisions, onExecute);
  const summary = stagedSummaryOf(decisions, staging.selection, staging.stage);
  const inconsistency = stagedInconsistency(decisions, staging.selection, staging.stage);
  return (
    <section aria-label="Decision" className="mt-8">
      <WorkSectionHead
        label="Decision"
        source="one open round · ticks are free — one Execute records and fires"
      />
      <div className="flex flex-col gap-3">
        {decisions.map((block, index) => {
          const key = blockKeyOf(block);
          const dependency =
            block.dependsOn !== null ? findOption(decisions, block.dependsOn) : null;
          return (
            <DecisionBlock
              key={key}
              block={block}
              index={index}
              selectedId={selectedOptionOf(block, staging.selection)?.id ?? null}
              tick={staging.stage[key] ?? null}
              dependencyTitle={dependency?.title ?? null}
              canRule={canRule}
              busy={staging.busy}
              onSelect={(optionId) => staging.select(key, optionId)}
              onStage={(tick) => staging.stageTick(key, tick)}
            />
          );
        })}
        <FollowOnNotes decisions={decisions} itemState={itemState} />
      </div>
      {canRule ? (
        <ExecuteBar
          summary={summary}
          inconsistency={inconsistency}
          busy={staging.busy}
          note={staging.note}
          onExecute={() => void staging.commit()}
        />
      ) : null}
    </section>
  );
}
